import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';


const CustomSolutionsSection = () => {
  const [activeSolution, setActiveSolution] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  const solutions = [
    {
      name: 'iTyres',
      industry: 'Шинный бизнес',
      icon: 'Truck',
      description: 'Полный цикл управления шинным центром от заказа до установки',
      before: {
        title: 'До СФЕРА',
        problems: ['Записи в Excel', 'Потеря заказов', 'Нет учета склада', 'Ручные расчеты']
      },
      after: {
        title: 'После СФЕРА',
        improvements: ['Онлайн-запись', 'Автоматизация заказов', 'Складской учет', 'Интеграция с 1С']
      },
      metrics: [
        { label: 'Рост выручки', value: '+45%', icon: 'TrendingUp' },
        { label: 'Экономия времени', value: '6ч/день', icon: 'Clock' },
        { label: 'Удержание клиентов', value: '92%', icon: 'Users' }
      ],
      color: 'primary'
    },
    {
      name: 'Warehouse',
      industry: 'Складская логистика',
      icon: 'Package',
      description: 'Управление складом, поставками и логистикой в реальном времени',
      before: {
        title: 'До СФЕРА',
        problems: ['Бумажные накладные', 'Ошибки инвентаризации', 'Долгая отгрузка', 'Нет аналитики']
      },
      after: {
        title: 'После СФЕРА',
        improvements: ['Цифровые документы', 'Автоучет остатков', 'Быстрая отгрузка', 'Прогнозы спроса']
      },
      metrics: [
        { label: 'Точность учета', value: '99.5%', icon: 'Target' },
        { label: 'Скорость отгрузки', value: '-60%', icon: 'Zap' },
        { label: 'Снижение потерь', value: '-75%', icon: 'TrendingDown' }
      ],
      color: 'accent'
    },
    {
      name: 'Service',
      industry: 'Сервисный центр',
      icon: 'Wrench',
      description: 'Управление ремонтами, запчастями и клиентским сервисом',
      before: {
        title: 'До СФЕРА',
        problems: ['Потеря заявок', 'Нет истории ремонтов', 'Долгий поиск запчастей', 'Низкая лояльность']
      },
      after: {
        title: 'После СФЕРА',
        improvements: ['Все заявки в системе', 'История по каждому клиенту', 'Каталог запчастей', 'Программа лояльности']
      },
      metrics: [
        { label: 'Повторные обращения', value: '+68%', icon: 'RefreshCw' },
        { label: 'Скорость ремонта', value: '-40%', icon: 'Timer' },
        { label: 'NPS', value: '8.7/10', icon: 'Star' }
      ],
      color: 'secondary'
    }
  ];

  return (
    <section ref={sectionRef} className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-background via-card to-background" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-4xl sm:text-5xl font-black text-foreground mb-4">
            Решения под ваш бизнес
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Реальные кейсы компаний, которые трансформировали свой бизнес с СФЕРА
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {solutions?.map((solution, index) => (
            <button
              key={index}
              onClick={() => setActiveSolution(index)}
              className={`glassmorphism rounded-xl px-6 py-4 transition-all duration-300 ${
                activeSolution === index 
                  ? `border-2 border-${solution?.color} neon-glow-${solution?.color} scale-105` 
                  : 'hover:scale-105'
              } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-lg bg-${solution?.color}/10 flex items-center justify-center`}>
                  <Icon name={solution?.icon} size={20} color={`var(--color-${solution?.color})`} />
                </div>
                <div className="text-left">
                  <div className="font-bold text-foreground">{solution?.name}</div>
                  <div className="text-xs text-muted-foreground">{solution?.industry}</div>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className={`glassmorphism-strong rounded-2xl p-8 transition-all duration-1000 delay-300 ${
          isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}>
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <div className={`w-16 h-16 rounded-xl bg-${solutions?.[activeSolution]?.color}/10 flex items-center justify-center`}>
                <Icon 
                  name={solutions?.[activeSolution]?.icon} 
                  size={32} 
                  color={`var(--color-${solutions?.[activeSolution]?.color})`} 
                />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-foreground">{solutions?.[activeSolution]?.name}</h3>
                <p className="text-muted-foreground">{solutions?.[activeSolution]?.industry}</p>
              </div>
            </div>
            <p className="text-foreground">{solutions?.[activeSolution]?.description}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="glassmorphism rounded-xl p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Icon name="XCircle" size={20} color="var(--color-error)" />
                <h4 className="font-bold text-foreground">{solutions?.[activeSolution]?.before?.title}</h4>
              </div>
              <ul className="space-y-2">
                {solutions?.[activeSolution]?.before?.problems?.map((problem, i) => (
                  <li key={i} className="flex items-start space-x-2">
                    <Icon name="Minus" size={16} color="var(--color-error)" className="mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{problem}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="glassmorphism rounded-xl p-6 border-2 border-success/20">
              <div className="flex items-center space-x-2 mb-4">
                <Icon name="CheckCircle2" size={20} color="var(--color-success)" />
                <h4 className="font-bold text-foreground">{solutions?.[activeSolution]?.after?.title}</h4>
              </div>
              <ul className="space-y-2">
                {solutions?.[activeSolution]?.after?.improvements?.map((improvement, i) => (
                  <li key={i} className="flex items-start space-x-2">
                    <Icon name="Check" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground">{improvement}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {solutions?.[activeSolution]?.metrics?.map((metric, i) => (
              <div key={i} className="glassmorphism rounded-xl p-6 text-center">
                <div className={`w-12 h-12 mx-auto mb-3 rounded-lg bg-${solutions?.[activeSolution]?.color}/10 flex items-center justify-center`}>
                  <Icon name={metric?.icon} size={24} color={`var(--color-${solutions?.[activeSolution]?.color})`} />
                </div>
                <div className={`text-3xl font-black text-${solutions?.[activeSolution]?.color} mb-1`}>
                  {metric?.value}
                </div>
                <div className="text-xs text-muted-foreground">{metric?.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomSolutionsSection;